import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Building2, ShieldAlert, RefreshCw, CheckCircle2, XCircle, Lock,
  Euro, FileText, Save, ExternalLink, MapPin, Home,
} from 'lucide-react';

// ★ 2026-08-11 (Fase 37) — Pagina CRM per gestire status appartamenti
// ★ 2026-08-12 (Fase 43) — Lista progetti resa DINAMICA: fetch da /api/public/properties
//   Filtra progetti con più di 1 unità (multi-unit) leggendo /api/public/projects/{slug}/units
// Solo admin/amministrativo. Permette di:
// - Vedere tutti gli appartamenti dei progetti multi-unit (La Mar, Narea, futuri)
// - Cambiare status (available / reserved / sold / off_market)
// - Modificare prezzo, note, docs
// - Vedere per ognuno se ha un lead associato (reserved_by_lead_id)

// Fallback statico: progetti multi-unit noti (usato mentre carica /api/public/properties)
const FALLBACK_PROJECTS = [
  { slug: 'la-mar-residence', name: 'La Mar Residence', flag: '🇦🇱', country: 'Albania' },
  { slug: 'narea',            name: 'Narea',            flag: '🇦🇱', country: 'Albania' },
];

// Mapping paese → flag (deriva flag da location.contains(country))
const COUNTRY_FLAGS = {
  'Albania': '🇦🇱',
  'Indonesia': '🇮🇩',
  'Thailandia': '🇹🇭',
};

function inferFlag(location = '', name = '') {
  const s = `${location} ${name}`.toLowerCase();
  if (s.includes('albania')) return '🇦🇱';
  if (s.includes('indonesia') || s.includes('lombok')) return '🇮🇩';
  if (s.includes('thailand') || s.includes('chumphon')) return '🇹🇭';
  return '🏗️';
}

const STATUS_OPTIONS = [
  { value: 'available',  label: 'Disponibile', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'reserved',   label: 'Prenotato',   color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'sold',       label: 'Venduto',     color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'off_market', label: 'Fuori mercato', color: 'bg-slate-100 text-slate-700 border-slate-300' },
];

const fmtEUR = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};

export default function PropertyUnitsAdminPage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'amministrativo'].includes(user?.role);

  // ★ 2026-08-12 (Fase 43) — Lista progetti multi-unit caricata dinamicamente dall'API
  const [projects, setProjects] = useState(FALLBACK_PROJECTS);
  const [projectSlug, setProjectSlug] = useState(FALLBACK_PROJECTS[0].slug);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [blockFilter, setBlockFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ status: 'available', notes: '', price_total: '' });
  const [saving, setSaving] = useState(false);

  // Fetch dinamico dei progetti multi-unit da /api/public/properties
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/public/properties');
        // API returns a plain array (list of properties)
        const list = Array.isArray(data) ? data : (Array.isArray(data?.properties) ? data.properties : []);
        // Filtra solo progetti Real Estate multi-unit (>=10 unit totali)
        const multiUnit = list
          .filter((p) => (p?.apartments?.total || 0) >= 10 && p?.slug)
          .map((p) => ({
            slug: p.slug,
            name: p.short_name || p.name,
            flag: inferFlag(p.location, p.name),
            country: (p.location || '').split(',').pop().trim() || 'Albania',
            total: p.apartments?.total || 0,
            available: p.apartments?.available || 0,
          }));
        if (cancelled) return;
        if (multiUnit.length) {
          setProjects(multiUnit);
          // Se il projectSlug corrente non è più valido, seleziona il primo.
          // ★ Round 2 qualità — updater funzionale: niente dipendenza su
          // `projectSlug` nell'effetto mount-only (fix exhaustive-deps).
          setProjectSlug((cur) => (
            multiUnit.find((p) => p.slug === cur) ? cur : multiUnit[0].slug
          ));
        }
      } catch (err) {
        // fallback statico già presente
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get('/property-units', { params: { project_slug: projectSlug } });
      setUnits(data?.units || []);
    } catch (err) {
      toast.error('Errore caricamento appartamenti');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, projectSlug]);

  useEffect(() => { load(); }, [load]);

  const blocks = useMemo(() => {
    // ★ 2026-08-14 (Fase 51.5) — Ordinamento naturale per numero (BLOCCO 1..22),
    // non alfabetico (che ordinerebbe BLOCCO 1 → BLOCCO 10 → BLOCCO 11 → BLOCCO 2).
    const unique = Array.from(new Set(units.map((u) => u.block).filter(Boolean)));
    return unique.sort((a, b) => {
      const na = parseInt(String(a).match(/\d+/)?.[0] || '0', 10);
      const nb = parseInt(String(b).match(/\d+/)?.[0] || '0', 10);
      if (na !== nb) return na - nb;
      return String(a).localeCompare(String(b));
    });
  }, [units]);

  const filtered = useMemo(() => {
    return units.filter((u) => {
      if (blockFilter !== 'all' && u.block !== blockFilter) return false;
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      return true;
    });
  }, [units, blockFilter, statusFilter]);

  const counts = useMemo(() => {
    const c = { available: 0, reserved: 0, sold: 0, off_market: 0 };
    for (const u of units) {
      c[u.status] = (c[u.status] || 0) + 1;
    }
    return c;
  }, [units]);

  // ★ 2026-08-14 (Fase 51 fix CRM) — Rileva dinamicamente quali colonne
  // extra mostrare in base ai campi presenti nel primo item dei dati.
  //   Lombok ha floor + caparra_eur + anticipo_eur + rata_eur + saldo_eur + roi_annual_eur
  //   La Mar / Narea hanno solo mq + price_total
  const hasFloors   = units.some((u) => u.floor !== undefined && u.floor !== null);
  const hasCaparra  = units.some((u) => u.caparra_eur != null);
  const hasAnticipo = units.some((u) => u.anticipo_eur != null);
  const hasRate     = units.some((u) => u.rata_eur != null);
  const hasSaldo    = units.some((u) => u.saldo_eur != null);
  const hasRoi      = units.some((u) => u.roi_annual_eur != null);

  const openEdit = (unit) => {
    setEditTarget(unit);
    setEditForm({
      status: unit.status || 'available',
      notes: unit.notes || '',
      price_total: unit.price_eur ?? unit.price_total ?? '',
    });
  };

  const handleQuickStatus = async (unit, newStatus) => {
    // Quick change via dropdown senza aprire il modal
    if (unit.status === newStatus) return;
    try {
      await api.patch(`/property-units/${unit.id}`, { status: newStatus });
      toast.success(`${unit.code} ora ${STATUS_OPTIONS.find(o => o.value === newStatus)?.label}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const handleSave = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const payload = {
        status: editForm.status,
        notes: editForm.notes,
      };
      if (editForm.price_total && !isNaN(Number(editForm.price_total))) {
        payload.price_total = Number(editForm.price_total);
      }
      await api.patch(`/property-units/${editTarget.id}`, payload);
      toast.success(`Appartamento ${editTarget.code} aggiornato`);
      setEditTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6" data-testid="property-units-forbidden">
        <Alert variant="destructive">
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>Accesso negato</AlertTitle>
          <AlertDescription>Solo admin possono gestire gli appartamenti.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const project = projects.find((p) => p.slug === projectSlug) || projects[0];

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="property-units-admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[hsl(var(--primary))]" />
            <h1 className="font-serif text-2xl">Gestione Appartamenti</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Controlla lo status di ogni appartamento pubblicato sulla landing.
            Le modifiche sono immediate: cambia <b>Disponibile → Venduto</b> e l&apos;appartamento sparisce dalla lista pubblica.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="units-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <a
            href={`/resort/${projectSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="contents"
          >
            <Button variant="outline" size="sm" data-testid="units-view-public">
              <ExternalLink className="w-4 h-4 mr-2" />
              Vedi landing
            </Button>
          </a>
        </div>
      </div>

      {/* Info projecto */}
      <Card className="border-emerald-200/60 bg-gradient-to-br from-emerald-50/50 to-white">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="text-3xl">{project.flag}</div>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg text-[hsl(var(--primary))]">{project.name}</div>
            <div className="text-xs text-muted-foreground">
              <MapPin className="w-3 h-3 inline mr-0.5" /> {project.country} · slug: {project.slug}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border" data-testid="count-available">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {counts.available} disp.
            </Badge>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 border" data-testid="count-reserved">
              {counts.reserved} prenotati
            </Badge>
            <Badge className="bg-red-100 text-red-800 border-red-300 border" data-testid="count-sold">
              <XCircle className="w-3 h-3 mr-1" />
              {counts.sold} venduti
            </Badge>
            {counts.off_market > 0 && (
              <Badge className="bg-slate-100 text-slate-700 border-slate-300 border">
                <Lock className="w-3 h-3 mr-1" />
                {counts.off_market} fuori mercato
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filtri */}
      <Card className="bg-[var(--surface-1)]">
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Progetto</Label>
            <Select value={projectSlug} onValueChange={setProjectSlug}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-project">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.slug} value={p.slug}>{p.flag} {p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Blocco</Label>
            <Select value={blockFilter} onValueChange={setBlockFilter}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-block-admin">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {blocks.map((b) => (
                  <SelectItem key={b} value={b}>Blocco {b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-status-admin">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center text-muted-foreground">
            Nessun appartamento con i filtri selezionati.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="units-admin-table">
              <thead className="bg-[var(--surface-2)] border-b border-border">
                <tr className="text-left">
                  <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground">Codice</th>
                  <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground">Blocco</th>
                  {hasFloors && (
                    <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">Piano</th>
                  )}
                  <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">MQ</th>
                  <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">Prezzo</th>
                  {hasCaparra && (
                    <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">Caparra</th>
                  )}
                  {hasAnticipo && (
                    <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">Anticipo</th>
                  )}
                  {hasRate && (
                    <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">Rate</th>
                  )}
                  {hasSaldo && (
                    <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">Saldo</th>
                  )}
                  {hasRoi && (
                    <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">ROI/anno</th>
                  )}
                  <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground">Status</th>
                  <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground">Note</th>
                  <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-center">Modifica</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const status = STATUS_OPTIONS.find((s) => s.value === u.status) || STATUS_OPTIONS[0];
                  // ★ 2026-08-14 (Fase 51 fix CRM) — Fallback tra schemi diversi:
                  //   Lombok:  sqm / price_eur / caparra_eur / anticipo_eur / rata_eur / saldo_eur / roi_annual_eur
                  //   La Mar / Narea: mq  / price_total
                  const sqm      = u.sqm      ?? u.mq;
                  const price    = u.price_eur ?? u.price_total;
                  const caparra  = u.caparra_eur;
                  const anticipo = u.anticipo_eur;
                  const rata     = u.rata_eur;
                  const nRate    = u.n_rate;
                  const saldo    = u.saldo_eur;
                  const roi      = u.roi_annual_eur;
                  return (
                    <tr key={u.id} className="border-b border-border hover:bg-[var(--surface-1)]" data-testid={`unit-admin-row-${u.code}`}>
                      <td className="px-3 py-2.5 font-mono font-semibold text-[hsl(var(--primary))]">{u.code}</td>
                      <td className="px-3 py-2.5">
                        <Badge variant="outline" className="text-[10px]">{u.block}</Badge>
                      </td>
                      {hasFloors && (
                        <td className="px-3 py-2.5 text-right">{u.floor ?? '—'}</td>
                      )}
                      <td className="px-3 py-2.5 text-right">{sqm ? `${sqm} mq` : '—'}</td>
                      <td className="px-3 py-2.5 text-right font-serif font-semibold text-[hsl(var(--primary))]">
                        {price ? fmtEUR(price) : '—'}
                      </td>
                      {hasCaparra && (
                        <td className="px-3 py-2.5 text-right text-xs">{caparra ? fmtEUR(caparra) : '—'}</td>
                      )}
                      {hasAnticipo && (
                        <td className="px-3 py-2.5 text-right text-xs">{anticipo ? fmtEUR(anticipo) : '—'}</td>
                      )}
                      {hasRate && (
                        <td className="px-3 py-2.5 text-right text-xs">
                          {rata ? (
                            <>
                              {fmtEUR(rata)}
                              {nRate ? <span className="text-muted-foreground ml-1">×{nRate}</span> : null}
                            </>
                          ) : '—'}
                        </td>
                      )}
                      {hasSaldo && (
                        <td className="px-3 py-2.5 text-right text-xs">{saldo ? fmtEUR(saldo) : '—'}</td>
                      )}
                      {hasRoi && (
                        <td className="px-3 py-2.5 text-right text-xs text-[var(--gp-gold)] font-medium">
                          {roi ? fmtEUR(roi) : '—'}
                        </td>
                      )}
                      <td className="px-3 py-2.5">
                        <Select value={u.status} onValueChange={(v) => handleQuickStatus(u, v)}>
                          <SelectTrigger
                            className={`h-7 w-auto min-w-[125px] text-[11px] px-2 ${status.color} border`}
                            data-testid={`quick-status-${u.code}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2.5 max-w-[220px]">
                        <div className="text-[11px] text-muted-foreground truncate">
                          {u.notes || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(u)}
                          data-testid={`edit-btn-${u.code}`}
                          className="h-7 text-xs"
                        >
                          Modifica
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Edit modal */}
      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent className="max-w-md" data-testid="edit-unit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-[hsl(var(--primary))]" />
              Modifica {editTarget?.code}
            </DialogTitle>
            <DialogDescription>
              Blocco {editTarget?.block} · {editTarget?.sqm ?? editTarget?.mq ?? '—'} mq · Prezzo attuale {fmtEUR(editTarget?.price_eur ?? editTarget?.price_total)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Prezzo totale (opzionale, sovrascrive)</Label>
              <div className="relative mt-1">
                <Euro className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  type="number"
                  value={editForm.price_total}
                  onChange={(e) => setEditForm({ ...editForm, price_total: e.target.value })}
                  placeholder="Es. 80554.50"
                  className="pl-8"
                  data-testid="edit-price"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Note interne (opzionale)</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Es. Cliente Mario Rossi, contratto firmato 15/09, saldo previsto Q1 2027..."
                rows={3}
                className="mt-1"
                data-testid="edit-notes"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="edit-save">
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Salvataggio…' : 'Salva modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
